// ═══════════════════════════════════════════════
//   GAMES — launcher, reward, shared canvas/state
// ═══════════════════════════════════════════════
//
// Owns the shared minigame infrastructure used by every games/*.js:
//
//   • The shared <canvas id="mg-canvas"> + 2D context.
//   • Generic launch/start/stop dispatch driven by GAME_REGISTRY in data.js.
//   • Reward + cooldown logic (claimGameReward).
//   • Tiny opt-in helpers each game uses: addCleanup, startGameTimer,
//     setHP/loseHP, showGameOver. Adopting them keeps games short and
//     consistent — see the clowri-minigame skill for the full recipe.
//
// To add a minigame: add a row to GAME_REGISTRY in data.js, write
// games/<key>.js exposing window['start' + Cap(key)], import it in
// bootstrap.js, then add the key to the Supabase RPC `get_leaderboard`.

const {S, CONFIG, addPts, scheduleSave, openWin, GAME_LIST, GAME_REGISTRY} = window;

// ══════════════════════════════════════════
// Reward / cooldown
// ══════════════════════════════════════════
// Returns result text and awards pts only if record beaten OR 12h cooldown passed
const GAME_COOLDOWN = CONFIG.GAME_COOLDOWN_H * 60 * 60 * 1000;

function claimGameReward(key, score, maxPts) {
    const now = Date.now();
    const last = S.gameLastPlayed[key] || 0;
    const top = S.gameTopScores[key] ?? -1;
    const isRecord = score > top;
    const cooledDown = (now - last) >= GAME_COOLDOWN;
    S.gameLastPlayed[key] = now;
    if (window.triggerMission) {
        window.triggerMission(`play_game:${key}`);
        if (isRecord) window.triggerMission('beat_record');
    }
    if (isRecord) {
        S.gameTopScores[key] = score;
        // Dual-write: also push to the relational user_game_scores table.
        // The leaderboard reads from there; profiles.game_top_scores stays in
        // sync as the local cache and is dropped in a later cleanup PR.
        if (window.DB && window.DB.available()) {
            window.DB.getSession().then(({data: {session}}) => {
                if (!session) return;
                window.DB.upsertGameScore(session.user.id, key, score)
                    .then(() => {
                        const scoresWin = document.getElementById('w-scores');
                        if (scoresWin && scoresWin.style.display !== 'none' && window.fetchScores) {
                            window.fetchScores();
                        }
                    })
                    .catch(() => {});
            }).catch(() => {});
        }
    }
    scheduleSave();
    if (isRecord || cooledDown) {
        const p = maxPts;
        if (p > 0) addPts(p, `game:${key}`);
        const label = isRecord ? `🏆 NEW RECORD!` : `⏰ Comeback bonus!`;
        return `Score: ${score}${isRecord ? ` (prev best: ${top < 0 ? '—' : top})` : ''}  ${label} +${p} pts!`;
    }
    const hoursLeft = Math.ceil((GAME_COOLDOWN - (now - last)) / 3600000);
    return `Score: ${score} · Best: ${top}  —  Beat your record or wait ${hoursLeft}h for pts!`;
}

// ══════════════════════════════════════════
// Shared canvas + runtime state
// ══════════════════════════════════════════
window.canvas = document.getElementById('mg-canvas');
window.ctx = window.canvas.getContext('2d');
window.gameRunning = false;
window.gameScore = 0;
window.gameTimer = 0;
window.gameInterval = null;
window.timerInterval = null;
window.spawnInterval = null;
// HP system (used by setHP/loseHP/renderHP — opt in per game)
window.gameHP = 0;
window.gameMaxHP = 0;
// Star Catcher needs a basket position that survives reset (mousemove
// updates it even between rounds).
window.basket = {x: 150, w: 70, y: 295};

// ══════════════════════════════════════════
// Shared helpers — games opt in by calling these
// ══════════════════════════════════════════

// Cleanup queue: each game's start function registers teardown callbacks
// (event listeners, custom DOM nodes) via addCleanup(fn). The launcher
// drains the queue on stopGame() so adding a game can no longer "forget"
// to remove its listeners.
window._gameCleanups = [];
function addCleanup(fn) { window._gameCleanups.push(fn); }
function _runCleanups() {
    const fns = window._gameCleanups.splice(0);
    for (const fn of fns) {
        try { fn(); } catch (e) { console.warn('cleanup error:', e); }
    }
}

// Standard countdown timer that updates the mg-timer slot and calls onEnd
// at 0. Auto-registers its own clearInterval cleanup.
function startGameTimer(seconds, onEnd) {
    window.gameTimer = seconds;
    document.getElementById('mg-timer').textContent = seconds + 's';
    clearInterval(window.timerInterval);
    window.timerInterval = setInterval(() => {
        window.gameTimer--;
        document.getElementById('mg-timer').textContent = window.gameTimer + 's';
        if (window.gameTimer <= 0) {
            clearInterval(window.timerInterval);
            onEnd();
        }
    }, 1000);
    addCleanup(() => clearInterval(window.timerInterval));
}

// HP system — renders hearts in the mg-timer slot. Use when a game ends
// on damage rather than (or in addition to) a timer.
function setHP(n, max) {
    window.gameHP = n;
    window.gameMaxHP = max ?? n;
    renderHP();
}
function loseHP(amount = 1) {
    window.gameHP = Math.max(0, window.gameHP - amount);
    renderHP();
    return window.gameHP;
}
function renderHP() {
    const filled = '♥'.repeat(window.gameHP);
    const empty  = '♡'.repeat(Math.max(0, window.gameMaxHP - window.gameHP));
    const el = document.getElementById('mg-timer');
    if (el) el.textContent = filled + empty;
}

// End-screen renderer — every game finishes with this same call.
function showGameOver(msg) {
    document.getElementById('mg-result-text').textContent = msg;
    document.getElementById('mg-over').classList.add('show');
}

// Look up a registry entry by key (or null).
function getGameDef(key) {
    return GAME_REGISTRY.find(g => g.key === key) || null;
}

// Translate a mouse or touch event to canvas-local coordinates,
// correcting for CSS scaling when the canvas display size differs from its
// internal resolution (e.g. when width:100% stretches a 364×320 canvas).
function canvasPos(e) {
    const r   = canvas.getBoundingClientRect();
    const sx  = canvas.width  / r.width;
    const sy  = canvas.height / r.height;
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - r.left) * sx, y: (src.clientY - r.top) * sy };
}

// ══════════════════════════════════════════
// Generic launcher / start / stop
// ══════════════════════════════════════════

function _capKey(key) { return key.charAt(0).toUpperCase() + key.slice(1); }

function launchGame(key) {
    stopGame(); // clean up FIRST before touching S.currentGame
    const def = getGameDef(key);
    if (!def || def.link) return;
    S.currentGame = key;
    openWin('w-minigame');
    document.getElementById('mg-url').textContent = 'www.konbini-games.bun/' + key;
    document.getElementById('mg-start-title').textContent = def.title || def.name || key;
    document.getElementById('mg-start-sub').textContent   = def.subtitle || def.objective || '';
    document.getElementById('mg-score').textContent = '0';
    document.getElementById('mg-timer').textContent = '';
    // Store key on button so startGame always reads the right one
    document.getElementById('mg-start-btn').dataset.game = key;
    document.getElementById('mg-start').classList.add('show');
    document.getElementById('mg-over').classList.remove('show');
}

function startGame() {
    // Tear down anything left over from a previous round (e.g. user clicked
    // "Play Again" without going through stopGame). Drains addCleanup queue
    // and clears shared intervals so the next round starts clean.
    _runCleanups();
    clearInterval(window.gameInterval);
    clearInterval(window.timerInterval);
    clearInterval(window.spawnInterval);
    window.gameInterval = null;
    window.timerInterval = null;
    window.spawnInterval = null;

    // Read from explicit data attr — immune to stale S.currentGame
    const btn = document.getElementById('mg-start-btn');
    if (btn && btn.dataset.game) S.currentGame = btn.dataset.game;
    document.getElementById('mg-start').classList.remove('show');
    document.getElementById('mg-over').classList.remove('show');
    window.gameScore = 0;
    document.getElementById('mg-score').textContent = '0';
    const fnName = 'start' + _capKey(S.currentGame);
    const fn = window[fnName];
    if (typeof fn !== 'function') {
        console.warn('No start function for game:', S.currentGame, '(expected window.' + fnName + ')');
        return;
    }
    fn();
    S.gamesPlayed.add(S.currentGame);
    if (S.gamesPlayed.size >= 3) window.unlockNick('certified');
}

// Universal reset — drains addCleanup() callbacks, clears canvas + overlays.
// Per-game teardown lives in each game module via addCleanup(); this
// function no longer needs to know about any specific game.
function stopGame() {
    window.gameRunning = false;
    clearInterval(window.gameInterval);
    clearInterval(window.timerInterval);
    clearInterval(window.spawnInterval);
    window.gameInterval = null;
    window.timerInterval = null;
    window.spawnInterval = null;
    _runCleanups();
    if (window.ctx) window.ctx.clearRect(0, 0, window.canvas.width, window.canvas.height);
    document.getElementById('mg-start').classList.add('show');
    document.getElementById('mg-over').classList.remove('show');
}

// ══════════════════════════════════════════
// GAMES LIST UI — filter + render
// ══════════════════════════════════════════
let gamesFilter = 'all';
let gamesSearch = '';

function openGames() {
    openWin('w-games');
    renderGamesList();
}

function setGamesFilter(type) {
    gamesFilter = type;
    document.querySelectorAll('.games-filter').forEach(b => {
        b.classList.toggle('on', b.dataset.filter === type);
    });
    renderGamesList();
}

function setGamesSearch(q) {
    gamesSearch = (q || '').trim().toLowerCase();
    renderGamesList();
}

function _gameAction(item) {
    const def = getGameDef(item.key);
    if (item.link) {
        if (def && def.linkOpener) return `${def.linkOpener}()`;
        return `openWin('${(def && def.linkWin) || 'w-tama'}')`;
    }
    return `launchGame('${item.key}')`;
}

function _todaysPick() {
    const playable = GAME_LIST.filter(i => !i.link);
    if (!playable.length) return null;
    const d = new Date();
    const dayOfYear = Math.floor((d - new Date(d.getFullYear(), 0, 0)) / 86400000);
    return playable[dayOfYear % playable.length];
}

function _heroHTML(item) {
    const best = (S.gameTopScores || {})[item.key];
    const bestBadge = (best != null && best >= 0)
        ? `<span class="games-badge">🏆 BEST ${Number(best).toLocaleString()}</span>`
        : `<span class="games-badge">🆕 NEW TO YOU</span>`;
    return `
        <div class="games-hero">
            <span class="games-hero-spark">✦</span>
            <div class="games-hero-tile">${item.icon}</div>
            <div class="games-hero-body">
                <span class="games-hero-pick">★ TODAY'S PICK</span>
                <div class="games-hero-name">${item.name.toUpperCase()}</div>
                <div class="games-hero-desc">${item.desc} ✦ ${item.pts}</div>
                <div class="games-hero-badges">
                    ${bestBadge}
                    <span class="games-badge">★ LEADERBOARD</span>
                    <span class="games-badge hot">🔥 HOT TODAY</span>
                </div>
            </div>
            <div class="games-hero-side">
                <button class="games-hero-play" onclick="${_gameAction(item)}">▶ PLAY NOW</button>
                <button class="games-hero-secondary" onclick="openWin('w-scores')">HISCORES</button>
            </div>
        </div>`;
}

function _cardHTML(item) {
    const cat = item.cat || 'arcade';
    const catLabel = cat === 'care' ? 'APPS' : cat.toUpperCase();
    const isApp = !!item.link;
    const best = (S.gameTopScores || {})[item.key];
    const bestStr = (best != null && best >= 0)
        ? `🏆 ${Number(best).toLocaleString()}`
        : '🏆 0';
    const bestCls = (best != null && best >= 0) ? '' : ' empty';
    // NEW / UPD badges: compare each game's added/updated date against the
    // user's PREVIOUS login (S.prevLoginAt — auth.js shifts this in
    // enterDesktop). First-time users (prev=0) see no badges by design.
    const dates = (window.GAME_DATES || {})[item.key] || {};
    const prev = S.prevLoginAt || 0;
    const addedTs   = dates.added   ? new Date(dates.added  ).getTime() : 0;
    const updatedTs = dates.updated ? new Date(dates.updated).getTime() : 0;
    const isNew     = prev > 0 && addedTs   > prev;
    const isUpdated = prev > 0 && !isNew && updatedTs > prev;
    const newBadge  = isNew
        ? `<span class="game-card-new">NEW</span>`
        : (isUpdated ? `<span class="game-card-new game-card-upd">UPD</span>` : '');
    const cardCls = isApp ? 'game-card app' : 'game-card';
    const cue = isApp ? '→' : '▶';
    return `
        <div class="${cardCls}" role="button" tabindex="0" onclick="${_gameAction(item)}" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();${_gameAction(item)}}">
            ${newBadge}
            <div class="game-card-head">
                <div class="game-card-tile cat-${cat}">${item.icon}</div>
                <div class="game-card-meta">
                    <div class="game-card-name">${item.name}</div>
                    <div class="game-card-cat cat-${cat}">${catLabel}</div>
                </div>
            </div>
            <div class="game-card-desc">${item.desc}</div>
            <div class="game-card-foot">
                <span class="game-card-best${bestCls}">${bestStr}</span>
                <span class="game-card-cue">${cue}</span>
            </div>
        </div>`;
}

function renderGamesList() {
    const root = document.getElementById('games-scroll');
    if (!root) return;
    const countEl = document.getElementById('games-count');

    let items = gamesFilter === 'all'
        ? GAME_LIST
        : GAME_LIST.filter(i => i.cat === gamesFilter);
    if (gamesSearch) {
        items = items.filter(i =>
            i.name.toLowerCase().includes(gamesSearch) ||
            (i.desc || '').toLowerCase().includes(gamesSearch));
    }

    if (countEl) countEl.textContent = GAME_LIST.length;

    let html = '';
    const showHero = (gamesFilter === 'all' && !gamesSearch);
    if (showHero) {
        const pick = _todaysPick();
        if (pick) html += _heroHTML(pick);
        const rest = items.filter(i => i.key !== (pick && pick.key));
        if (rest.length) {
            html += `<div class="games-grid">${rest.map(_cardHTML).join('')}</div>`;
        }
    } else if (!items.length) {
        html = `<div class="games-empty">No games match.</div>`;
    } else {
        html = `<div class="games-grid">${items.map(_cardHTML).join('')}</div>`;
    }

    root.innerHTML = html;
}

window.canvasPos        = canvasPos;
window.claimGameReward  = claimGameReward;
window.launchGame       = launchGame;
window.startGame        = startGame;
window.stopGame         = stopGame;
window.openGames        = openGames;
window.setGamesFilter   = setGamesFilter;
window.setGamesSearch   = setGamesSearch;
window.renderGamesList  = renderGamesList;
window.addCleanup       = addCleanup;
window.startGameTimer   = startGameTimer;
window.setHP            = setHP;
window.loseHP           = loseHP;
window.renderHP         = renderHP;
window.showGameOver     = showGameOver;
window.getGameDef       = getGameDef;
